<template>
    <DsCard class="client_contact_container mb-3 text-content-primary">
        <header class="mb-2 flex h-8 items-center justify-between">
            <h3 class="client-heading text-content-md font-semibold">Client</h3>
            <DsIconButton
                v-if="formIndex > 0 || isTpsOrder"
                icon="fa-regular fa-trash-can"
                size="sm"
                tooltipText="Remove client"
                @click="$emit('removeClient')"
            />
        </header>
        <ClientFormFields
            v-model:client="client"
            class="grid grid-cols-12 gap-x-3"
            :clientAttrs
            :clientErrors="clientInfoErrors.get(`${formIndex}`) ?? createEmptyErrors()"
            @fieldInput="handleFieldInput"
        />
        <!-- @todo [CLENG-1942] remove hidden inputs needed for form submission -->
        <input
            class="client-role col-span-12"
            :name="`role[${formIndex}]`"
            type="hidden"
            :value="client.role || ClientTypeEnum.OTHER"
        />
        <input
            :name="`phone[${formIndex}]`"
            type="hidden"
            :value="client.phone"
        />
    </DsCard>
</template>

<script setup lang="ts">
import { updateRecipientsLists } from '@/company-portal/add-file';
import { handleEmailChangeForSigners } from '@/company-portal/add-file/add-file-documents';
import type { Client } from '@/company-portal/add-file/add-file.types';
import { clientInfoErrors } from '@/company-portal/add-file/index';
import { ADD_CLIENT_FORM_SELECTOR } from '@/company-portal/add-file/selectors';
import type { ClientAttrs } from '@/components/Views/Company/File/Client/client-info-form.types';
import { ClientValidationErrorKeys } from '@/components/Views/Company/File/Client/client-info-form.types';
import { createEmptyErrors } from '@/components/Views/Company/File/Client/client-info-form.validation';
import ClientFormFields from '@/components/Views/Company/File/Client/ClientFormFields.vue';
import { ClientTypeEnum } from '@/components/Views/Company/File/file.types';
import DsCard from '@ds/DsCard.vue';
import DsIconButton from '@ds/DsIconButton.vue';
import { nextTick } from 'vue';

const props = defineProps<{
    formIndex: number;
    isTpsOrder: boolean;
}>();

defineEmits<{
    removeClient: [];
}>();

const client = defineModel<Client>('client', { required: true });

const clearFieldError = (field: ClientValidationErrorKeys) => {
    const clientError = clientInfoErrors.get(`${props.formIndex}`);
    if (clientError) {
        clientError[field] = '';
    }
};

const handleFieldInput = (field: ClientValidationErrorKeys) => {
    clearFieldError(field);
    nextTick(() => {
        if (field === ClientValidationErrorKeys.EMAIL) {
            handleEmailChangeForSigners(`${props.formIndex}`);
        }
        updateRecipientsLists(ADD_CLIENT_FORM_SELECTOR);
    });
};

const clientAttrs: ClientAttrs = {
    first: {
        class: 'client-first col-span-12 sm:col-span-6 sm:col-start-1',
        name: `first[${props.formIndex}]`,
    },
    last: {
        class: 'client-last col-span-12 sm:col-span-6',
        name: `last[${props.formIndex}]`,
    },
    email: {
        class: 'client-email col-span-12',
        name: `email[${props.formIndex}]`,
    },
    phone: {
        class: 'client-phone col-span-8 sm:col-span-6',
    },
    role: {
        class: 'col-span-12 sm:col-span-6 md:col-span-4',
    },
};
</script>

<style scoped>
/* appends a number after "Client" in the heading (e.g. "Client 1", "Client 2", etc.) */
h3::after {
    counter-increment: client-heading;
    content: ' ' counter(client-heading);
}
</style>
